import { useRef, useState, useEffect } from "react";
import { motion, useInView, useMotionValue, useSpring, animate } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

// Line-by-line masked reveal for headings
export const MaskText = ({ lines = [], className = "", delay = 0, as = "h2" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const Tag = motion[as] || motion.h2;
  return (
    <Tag ref={ref} className={className} aria-label={lines.join(" ")}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden" aria-hidden="true">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={inView ? { y: "0%" } : { y: "110%" }}
            transition={{ duration: 0.9, ease: EASE, delay: delay + i * 0.09 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

// Generic scroll reveal
export const Reveal = ({ children, className = "", delay = 0, y = 34 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-8% 0px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
};

// Magnetic hover wrapper (for buttons / links)
export const Magnetic = ({ children, strength = 0.4, className = "" }) => {
  const ref = useRef(null);
  const x = useSpring(0, { stiffness: 200, damping: 15 });
  const y = useSpring(0, { stiffness: 200, damping: 15 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - (rect.left + rect.width / 2)) * strength);
    y.set((e.clientY - (rect.top + rect.height / 2)) * strength);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };
  return (
    <motion.div
      ref={ref}
      className={className}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ x, y }}
    >
      {children}
    </motion.div>
  );
};

// Animated number counter
export const Counter = ({ value, suffix = "", className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2,
      ease: EASE,
      onUpdate: (v) => setDisplay(Math.floor(v)),
    });
    return () => controls.stop();
  }, [inView, value]);
  return (
    <span ref={ref} className={className}>
      {display}
      {suffix}
    </span>
  );
};

export const SectionLabel = ({ children, tone = "teal", className = "" }) => (
  <span className={`overline text-xs inline-flex items-center gap-2 ${className}`}>
    <span
      className="inline-block h-[6px] w-[6px] rounded-full"
      style={{ background: tone === "teal" ? "var(--dm-teal)" : "var(--dm-maroon)" }}
    />
    {children}
  </span>
);
